import { useEffect, useRef, useState, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { Box, Button, Skeleton } from "@mui/material";

import { briefInfoType, userState, lastListCookieId } from "../../../interface/MainInterface";
import { HttpUnAuthApi } from "../../../interface/unauth-api";
import CardBrief from "./CardBrief";
import PopAddScrap from "../../scrap/PopAddScrap";

// CSS
import "./BriefList.css";
import Toast from "../../../utils/Toast";
import PopChangeCtg from "../../brief/PopChangeCtg";
import { useWindowSize } from "../../../utils/useWindowSize";

interface propsType {
  userState: userState;
  selectedPeriod: string | null;
  selectedKeywords: any;
  selectedCategory: any;
  briefList: any;
  setBriefList: any;
  queryText: string;
  searchText: string;
  setSearchText: any;
  lastEvalKey: any;
  setLastEvalKey: any;
  nowPageSize: number;
  setNowPageSize: any;
  lastScrollPos: number;
  setLastScrollPos: any;
  needRefresh: boolean;
  setNeedRefresh: any;
  callback: any;
}

const unAuthAPI = new HttpUnAuthApi();
const pageSize = 9;

const BriefList = (props: propsType, ref: any) => {
  const toastRef: any = useRef();
  const navigate = useNavigate();
  const windowSize = useWindowSize();

  const popScrapRef: any = useRef(); // 스크랩함
  const popCategoryRef: any = useRef(); // 스크랩함
  const scrollRef: any = useRef(0); // 뒤로가기 마지막 스크롤 저장

  const [isLoading, setIsLoading] = useState(false); // 로딩 (스켈레톤 제어용)

  const [filteredBriefList, setFilteredBriefList] = useState<briefInfoType[]>([]); // 카테고리, 키워드로 필터링된 브리프 목록
  const [displayBriefList, setDisplayBriefList] = useState<any>([]); // 9개씩 화면에 보여줄 브리프 목록
  const [selectedBriefs, setSelectedBriefs] = useState<any>([]); // 브리프 선택 체크박스 스크랩용
  const [openScrap, setOpenScrap] = useState(false); // 스트랩 버튼 제어용
  const [isFirst, setIsFirst] = useState(true); // 페이지 되돌아올때 1번만 이전 스크롤 위치로 이동시키기 위함

  useEffect(() => {
    window.addEventListener("scroll", saveScrollPosition);
    return () => {
      window.removeEventListener("scroll", saveScrollPosition);
      props.setLastScrollPos(scrollRef.current); // 언마운트 : 즉 브리프 상세보기 들어갈때 마지막 스크롤 위치를 저장
      setIsLoading(false);
      setOpenScrap(false);
      setSelectedBriefs([]);
      setIsFirst(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //---------------------------------------------------------------------------------------
  // 브리프 목록 처리 DB조회 속도를 위해서 화면에 최초 리스트를 뿌려준후
  // -> more 조회 -> 키워드, 카테고리별 필터링 -> display count로 더보기 버튼 처리
  //---------------------------------------------------------------------------------------
  const init_state = () => {
    setSelectedBriefs([]);
    props.setLastScrollPos(0);
    props.setNowPageSize(pageSize);
    props.setBriefList([]);
    props.setLastEvalKey("");
  };
  // 브리프 조회
  const get_brief_list = async (isNewSearch: boolean = false) => {
    // 최초검색 초기화
    init_state();

    const param: any = {
      command: "get_brief_list_page",
    };
    const search_dtm = _make_search_dtm();
    param.from_dtm = search_dtm.from;
    param.to_dtm = search_dtm.to;

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      let temp: any = [];
      for (const brief of res.response.brief_list) {
        if (brief.ai_title && brief.ai_title.trim() !== "") {
          temp.push(brief);
        }
      }
      props.setBriefList(temp);
      props.setLastEvalKey(res.response.last_eval_key); // 다음 데이터를 위한
    } else {
      console.error("[ERROR] get_brief_list : ", res.response.error_msg);
    }
    setIsLoading(false);
  };

  const get_brief_list_more = async (isNewSearch: boolean = false) => {
    const param: any = {
      command: "get_brief_list_page",
      last_eval_key: props.lastEvalKey,
    };
    const search_dtm = _make_search_dtm();
    param.from_dtm = search_dtm.from;
    param.to_dtm = search_dtm.to;

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      let temp: any = [...props.briefList];
      for (const brief of res.response.brief_list) {
        if (brief.ai_title && brief.ai_title.trim() !== "") {
          temp.push(brief);
        }
      }
      props.setBriefList(temp);
      props.setLastEvalKey(res.response.last_eval_key); // 다음 데이터를 위한
    } else {
      console.error("[ERROR] get_brief_list : ", res.response.error_msg);
    }
  };

  // 상단 검색툴로 특정 키워드 검색시
  const get_brief_search_list = async () => {
    // 최초검색 초기화
    init_state();

    const param: any = {
      command: "get_brief_search_list_page",
      keyword: props.queryText,
    };

    const search_dtm = _make_search_dtm();
    param.from_dtm = search_dtm.from;
    param.to_dtm = search_dtm.to;

    setIsLoading(true);

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      let temp: any = [];
      for (const brief of res.response.brief_list) {
        if (brief.ai_title && brief.ai_title.trim() !== "") {
          temp.push(brief);
        }
      }
      props.setBriefList(temp);
      props.setLastEvalKey(res.response.last_eval_key); // 다음 데이터를 위한 키 업데이트
    } else {
      console.error("[ERROR] get_brief_list : ", res.response.error_msg);
    }
    setIsLoading(false);
  };

  const get_brief_search_list_more = async () => {
    const param: any = {
      command: "get_brief_search_list_page",
      keyword: props.queryText,
      last_eval_key: props.lastEvalKey,
    };

    const search_dtm = _make_search_dtm();
    param.from_dtm = search_dtm.from;
    param.to_dtm = search_dtm.to;

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      let temp: any = [...props.briefList];
      for (const brief of res.response.brief_list) {
        if (brief.ai_title && brief.ai_title.trim() !== "") {
          temp.push(brief);
        }
      }
      props.setBriefList(temp);
      props.setLastEvalKey(res.response.last_eval_key); // 다음 데이터를 위한 키 업데이트
    } else {
      console.error("[ERROR] get_brief_list : ", res.response.error_msg);
    }
  };

  // 브리프 리스트를 선택한 키워드, 카테고리에 따라서 필터한다.
  useEffect(() => {
    let lastList: any = [];
    let temp: any = [];
    for (const info of props.briefList) {
      if (checkFilter(info)) {
        if (info.show_pk === "design") {
          continue;
        }
        if (props.selectedCategory === null && (info.show_pk === 'IT' || info.show_pk === 'tech')) {
          continue;
        }
        lastList = [...lastList, `${info.show_pk}/${info.sk}`];
        temp.push(info);
      }
    }
    // console.log('selectedCategory: ', props.selectedCategory);
    setFilteredBriefList(temp);
    window.localStorage.setItem(lastListCookieId, lastList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.briefList, props.selectedCategory, props.selectedKeywords]);

  // 1차적으로 리스트를 뿌려준뒤 나머지 리스트를 모두 가져온다.
  useEffect(() => {
    if (props.lastEvalKey !== "") {
      if (props.queryText !== "") {
        get_brief_search_list_more();
      } else {
        get_brief_list_more();
      }
    }
    let temp: any = [];
    let inCount: number = 0;
    for (const info of filteredBriefList) {
      if (inCount < props.nowPageSize) {
        temp.push(info);
        inCount += 1;
      }
    }
    setDisplayBriefList(temp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBriefList, props.nowPageSize]);

  // 검색조건에 따른 브리프 필터링
  const checkFilter = (info: briefInfoType) => {
    let finalOk = false;
    // 선택된 키워드에 해당하는지 확인
    let keywordOk = false;
    if (props.selectedKeywords === null) keywordOk = true;
    else if (props.selectedKeywords.length === 0) keywordOk = true;
    else {
      if (props.queryText !== "") keywordOk = true;
      else {
        for (const key of props.selectedKeywords) {
          // 전체 키워드 중 해당사항이 있을때
          if (info.ai_keyword?.indexOf(key) > -1) {
            keywordOk = true;
            break;
          }
        }
      }
    }

    // 선택된 카테고리에 해당하는지 확인
    let cateOk = false;
    if (props.selectedCategory === null) cateOk = true;
    else {
      if (props.selectedCategory.key === info.show_pk) cateOk = true;
      else cateOk = false;
    }

    if (keywordOk && cateOk) finalOk = true;
    return finalOk;
  };

  //---------------------------------------------------------------------------------------
  // Scroll 처리 -> 스크롤 변경시 마지막 스크롤 위치를 ref에 저장 -> 페이지 이동전 마지막 스크롤 위치를 root에 전송
  // 최초 페이지 로딩시 마지막 스크롤로 이동
  //---------------------------------------------------------------------------------------
  useEffect(() => {
    // isFirst : 한번만 스크롤을 동기화 해야함. 이 설정이 없으면 브리프목록 불러오면서 스크롤 이동할때마다 계속 위로 올라감
    if (displayBriefList.length > 0 && isFirst) {
      window.scrollTo({
        top: props.lastScrollPos, // 설정한 위치로 이동
      });
      setIsFirst(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayBriefList]);

  // 스크롤 변경시 최종위치 저장
  // State를 사용하지 않는 이유는 변경될때마다 렌더링이 일어나서 ref로 저장함
  const saveScrollPosition = () => {
    scrollRef.current = window.scrollY;
  };

  //---------------------------------------------------------------------------------------
  // Scrap
  //---------------------------------------------------------------------------------------
  // 브리프 선택(체크박스)
  const selectBrief = (key: string) => {
    let temp = [...selectedBriefs];
    const index = temp.indexOf(key);
    if (index > -1) {
      temp.splice(index, 1);
    } else {
      temp.push(key);
    }
    setSelectedBriefs(temp);
  };

  // 선택한 브리프가 n개 이상이면 스크랩 버튼 활성화
  useEffect(() => {
    if (selectedBriefs.length > 0 && props.selectedKeywords) {
      setOpenScrap(true); // 스크랩 버튼 보이기
    } else {
      setOpenScrap(false);
    }

    // console.log("selectedBriefs ", selectedBriefs);
  }, [selectedBriefs, props.selectedKeywords]);

  const scrap = () => {
    popScrapRef.current.open(selectedBriefs);
  };

  //---------------------------------------------------------------------------------------
  // 기타
  //---------------------------------------------------------------------------------------
  // 브리프 바로가기
  const goBrief = (briefInfo: any) => {
    if (props.searchText !== undefined && props.searchText !== null && props.searchText !== "") {
      navigate(`/brief/${briefInfo.show_pk}/${briefInfo.sk}?result=${props.searchText}`);
    } else {
      navigate(`/brief/${briefInfo.show_pk}/${briefInfo.sk}?list`);
    }
  };

  // 브리프 삭제
  const deleteBrief = async (briefInfo: any) => {
    // 먼저 confirm을 처리하고, 이후에 비동기 코드 실행 (iOS 18.6.2 버전에서 동작하지 않아서 confirmed 처리)
    const confirmed = window.confirm("해당 브리프를 삭제하시겠습니까?");
    if (!confirmed) return;

    setIsLoading(true);
    const param: any = {
      command: "delete_brief",
      pk: briefInfo.pk,
      sk: briefInfo.sk,
      user_id: props.userState.id, 
    }

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      if (props.selectedPeriod !== null) {
        if (props.queryText === "") {
          // aitb
          props.setSearchText("");
          if (props.needRefresh) get_brief_list();
          else props.setNeedRefresh(true);
        } else {
          // Search
          if (props.searchText !== props.queryText) {
            props.setSearchText(props.queryText);
          }
          if (props.needRefresh) get_brief_search_list();
          else props.setNeedRefresh(true);
        }
      }
    } else {
      console.error(res.response.error_msg);
      toastRef.current?.toast("" + res.response.error_msg, "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
    }
    setIsLoading(false);
  }

  // 카테고리 변경
  const change_category = () => {
    popCategoryRef.current.open(selectedBriefs);
  };

  //--------------------------------------------------
  // Sub module functions
  //--------------------------------------------------
  // 검색 조건별 날짜 계산
  const _make_search_dtm = () => {
    let from_dtm = "";
    let to_dtm = "";
    // 검색 조건별 날짜 세팅
    if (props.selectedPeriod === "recent") {
      // 3일전
      from_dtm = dayjs().subtract(3, "day").format("YYYYMMDD");
      to_dtm = dayjs().add(1, "day").format("YYYYMMDD");
    } else if (props.selectedPeriod === "week") {
      // 1주일
      from_dtm = dayjs().subtract(7, "day").format("YYYYMMDD");
      to_dtm = dayjs().add(1, "day").format("YYYYMMDD");
    } else if (props.selectedPeriod === "month") {
      // 1주일
      from_dtm = dayjs().subtract(1, "month").format("YYYYMMDD");
      to_dtm = dayjs().add(1, "day").format("YYYYMMDD");
    } else if (props.selectedPeriod === "quarter") {
      // 3개월
      from_dtm = dayjs().subtract(3, "month").format("YYYYMMDD");
      to_dtm = dayjs().add(1, "day").format("YYYYMMDD");
    }
    return { from: from_dtm, to: to_dtm };
  };

  // 화면 로딩 트리거
  useEffect(() => {
    if (props.selectedPeriod !== null) {
      if (props.queryText === "") {
        // aitb
        props.setSearchText("");
        if (props.needRefresh) get_brief_list();
        else props.setNeedRefresh(true);
      } else {
        // Search
        if (props.searchText !== props.queryText) {
          props.setSearchText(props.queryText);
        }
        if (props.needRefresh) get_brief_search_list();
        else props.setNeedRefresh(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.queryText, props.searchText, props.selectedPeriod]);

  return (
    <>
      <div className={openScrap ? "maga-btn-wrap btn-scrap-open" : "maga-btn-wrap btn-scrap-close"}>
        <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
          <div className="makeMagBtn" style={{ height: "100%" }}>
            <button id="makeMagBtn" className="make-mag" onClick={scrap}>
              <img src="/images/icon-magazine-white.png" alt="" />
              <i>
                <span>스크랩하기</span>
              </i>
            </button>
            <button
              className="scrap-close"
              onClick={() => {
                setSelectedBriefs([]);
              }}
            ></button>
          </div>
          {props.userState.isSuperAdmin && windowSize.width > 1023 && (
            <div className="changeCtgBtn" style={{ height: "100%" }}>
              <button id="changeCtgBtn" className="change-ctg" onClick={change_category}>
                <img src="/images/icon-setting-white.png" alt="" />
                <i>
                  <span>카테고리 변경</span>
                </i>
              </button>
              {/* <button
                className="scrap-close"
                onClick={() => {
                  setSelectedBriefs([]);
                }}
              ></button> */}
            </div>
          )}
        </div>
      </div>
      <article className="article-02">
        <div className="brief-list-wrap">
          <div className="brief-list">
            <div className="brief-cont">
              <div className="main-brief">
                <dl>
                  <dd>
                    {isLoading && displayBriefList.length < 1
                      ? [...Array(9)].map((value: any, index) => (
                          <div key={`card-skeleton-${index}`} className="brief-box one-third">
                            <Skeleton
                              key={`brief-${index}`}
                              variant="rounded"
                              width={"100%"}
                              height={"220px"}
                              sx={{ borderRadius: "30px", mb: "32px" }}
                            />
                          </div>
                        ))
                      : displayBriefList.length > 0 && 
                        displayBriefList.map((data: briefInfoType, index: number) => (
                          <CardBrief
                            key={`brief-info-${index}`}
                            index={index}
                            briefInfo={data}
                            selectedKeywords={props.selectedKeywords}
                            taggedKeywords={null}
                            checked={selectedBriefs.indexOf(`${data.show_pk}/${data.sk}`) > -1}
                            callback={(payload: any) => {
                              if (payload.command === "selectBrief") {
                                selectBrief(payload.key);
                              } else if (payload.command === "goBrief") {
                                props.setNeedRefresh(false); //브리프 상세보기 클릭시 다시 목록으로 돌아올때 브리프 리스트를 재조회하지 않기 위함
                                goBrief(payload.briefInfo);
                              } else if (payload.command === "deleteBrief") {
                                deleteBrief(payload.briefInfo);
                              }
                            }}
                            queryText={props.queryText}
                            userState={props.userState}
                          />
                        ))}
                    {/* 마지막 CSS 위치 이상한 부분 교정     */}
                    {displayBriefList.length % 3 === 2 && <Box sx={{ width: "31.7%" }}></Box>}
                  </dd>
                </dl>
              </div>
            </div>
            {props.nowPageSize < filteredBriefList.length && (
              <>
              <div className="btn-more-list-wrap" >
                <div className="btn-more-list">
                  <button
                    // variant="text"
                    onClick={() => {
                      props.setNowPageSize(props.nowPageSize + pageSize);
                    }}
                    style={{ zIndex: 1, fontSize: "16px", fontWeight: "800" }}
                  >
                    <span>브리프 더보기</span>
                    <i style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "33px", height: "33px", marginLeft: "0px" }}>
                      <img src="/images/btn-add-list.png" style={{ width: "13px", height: "13px" }} />
                    </i>
                  </button>
                </div>
                <div className="gradient-bottom" />
              </div>
              </>

            )}
          </div>
        </div>
      </article>
      <PopAddScrap
        ref={popScrapRef}
        userState={props.userState}
        selectedKeywords={props.selectedKeywords}
        callback={(payload: any) => {}}
      />
      <PopChangeCtg
        ref={popCategoryRef}
        userState={props.userState}
        callback={(payload: any) => {}}
      />
      <Toast ref={toastRef} />
    </>
  );
};

export default forwardRef(BriefList);
